import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarX,
  CheckCircle2,
  ClipboardCheck,
  GraduationCap,
  Inbox,
  MessageSquare,
  Trophy,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AdminPage, ErrorState, Panel, Progress, Skeleton, StatusBadge } from '@/design/components';
import { formatDate, formatDateTime, timeLeftLabel } from '@/lib/dates';
import { DashboardSalesCard } from '@/features/sales/DashboardSalesCard';
import { useModules } from '@/access/useModule';
import { percent, useDashboard } from './api';

/**
 * דשבורד ההנהלה (PRD 5.12).
 *
 * הסדר הוא לפי דחיפות ולא לפי מודול: מה שיש לו דדליין קרוב למעלה,
 * מה שאפשר לדחות למחר למטה. המובילים בנקודות אחרונים, כי הם נחמדים
 * ולא דחופים.
 */
export function DashboardPage() {
  const [branchId, setBranchId] = useState<string | null>(null);
  const dashboard = useDashboard(branchId);
  // כרטיס של מודול כבוי אינו "מה דורש טיפול": אין לאן ללחוץ
  // ממנו, והמסך שהוא מפנה אליו כבר לא קיים
  const modules = useModules();

  if (dashboard.isPending) return <Skeleton className="m-8 h-64" />;
  if (dashboard.isError) return <ErrorState onRetry={() => void dashboard.refetch()} />;

  // ה RPC מחזיר null למי שאין לו אף הרשאת ניהול
  if (!dashboard.data) {
    return (
      <AdminPage title="דשבורד" subtitle="מה דורש טיפול">
        <p className="m-0 text-body text-muted">אין לך הרשאות ניהול, ולכן אין כאן מה להציג.</p>
      </AdminPage>
    );
  }

  const data = dashboard.data;
  const checklistsOpen = data.checklists.filter((row) => !row.completed_at);
  /**
   * "הכל מטופל" נספר רק ממודולים שדולקים.
   *
   * אחרת צ׳קליסט פתוח במודול כבוי היה מבטל את ההודעה בלי להציג
   * שום כרטיס, והמסך היה נשאר ריק ושותק — בלי "יש עבודה" ובלי
   * "אין עבודה".
   */
  const pending = [
    modules.schedule === true && data.missing_constraints.count > 0,
    modules.schedule === true && data.understaffed.count > 0,
    modules.messages === true && data.ack_messages.length > 0,
    modules.learning_courses === true && data.late_learning.length > 0,
    modules.ops_checklists === true && checklistsOpen.length > 0,
    modules.ops_requests === true && data.pending_requests.length > 0,
  ];
  const quiet = pending.every((item) => item === false);

  return (
    <AdminPage
      title="דשבורד"
      subtitle="מה דורש טיפול"
      actions={
        data.branches.length > 1 && (
          <select
            value={branchId ?? ''}
            onChange={(event) => setBranchId(event.target.value || null)}
            aria-label="סינון לפי סניף"
            className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
          >
            <option value="">כל הסניפים</option>
            {data.branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        )
      }
    >
      {quiet && (
        <div className="flex items-center gap-2 rounded-md border border-good bg-raised p-4">
          <CheckCircle2 size={24} strokeWidth={1.75} aria-hidden="true" className="text-good" />
          <span className="text-title font-medium">הכל מטופל. אין מה לעשות עכשיו.</span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {modules.schedule === true && data.missing_constraints.count > 0 && (
          <Card
            icon={CalendarX}
            title="לא הגישו אילוצים"
            count={data.missing_constraints.count}
            tone="warn"
            href="/admin/schedule"
            footer={
              data.missing_constraints.deadline
                ? timeLeftLabel(data.missing_constraints.deadline)
                : undefined
            }
          >
            <ul className="m-0 grid list-none gap-1 p-0">
              {data.missing_constraints.employees.slice(0, 6).map((employee) => (
                <li key={employee.employee_id} className="text-body-sm text-muted">
                  {employee.name}
                </li>
              ))}
              {data.missing_constraints.employees.length > 6 && (
                <li className="text-label text-muted">
                  ועוד {data.missing_constraints.employees.length - 6}
                </li>
              )}
            </ul>
          </Card>
        )}

        {modules.schedule === true && data.understaffed.count > 0 && (
          <Card
            icon={Users}
            title="משמרות עם חוסר"
            count={data.understaffed.count}
            tone="bad"
            href="/admin/schedule"
          >
            <ul className="m-0 grid list-none gap-1 p-0">
              {data.understaffed.shifts.slice(0, 6).map((shift, i) => (
                <li key={i} className="text-body-sm text-muted">
                  {formatDate(shift.date)} · {shift.shift} · {shift.assigned} מתוך {shift.required}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {modules.ops_checklists === true && checklistsOpen.length > 0 && (
          <Card
            icon={ClipboardCheck}
            title="צ׳קליסטים שלא הושלמו היום"
            count={checklistsOpen.length}
            tone="warn"
            href="/admin/ops/checklists"
          >
            <ul className="m-0 grid list-none gap-1 p-0">
              {checklistsOpen.slice(0, 6).map((row) => (
                <li key={`${row.branch_id}-${row.template_id}`} className="text-body-sm text-muted">
                  {row.branch} · {row.template} · {row.done} מתוך {row.total}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {modules.ops_requests === true && data.pending_requests.length > 0 && (
          <Card
            icon={Inbox}
            title="בקשות שממתינות"
            count={data.pending_requests.length}
            tone="warn"
            href="/admin/employees/requests"
          >
            <ul className="m-0 grid list-none gap-1 p-0">
              {data.pending_requests.slice(0, 6).map((request) => (
                <li key={request.request_id} className="text-body-sm text-muted">
                  {request.name} · {request.type} · {formatDate(request.created_at)}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {modules.messages === true && data.ack_messages.length > 0 && (
          <Card
            icon={MessageSquare}
            title="הודעות שלא אושרו"
            count={data.ack_messages.length}
            tone="warn"
          >
            <div className="grid gap-2">
              {data.ack_messages.slice(0, 4).map((message) => (
                <Link
                  key={message.message_id}
                  to={`/admin/messages/reports/${message.message_id}`}
                  className="grid gap-1 no-underline"
                >
                  <span className="truncate text-body-sm text-ink">{message.body}</span>
                  <Progress
                    value={message.acked}
                    max={message.audience}
                    label={`${message.acked} מתוך ${message.audience} אישרו (${percent(
                      message.acked,
                      message.audience,
                    )}%)`}
                  />
                </Link>
              ))}
            </div>
          </Card>
        )}

        {modules.learning_courses === true && data.late_learning.length > 0 && (
          <Card
            icon={GraduationCap}
            title="לומדות באיחור"
            count={data.late_learning.length}
            tone="bad"
            href="/admin/learning/assignments"
          >
            <ul className="m-0 grid list-none gap-1 p-0">
              {data.late_learning.slice(0, 6).map((row) => (
                <li key={row.assignment_id} className="text-body-sm text-muted">
                  {row.title} · {row.pending} לא סיימו · היה עד {formatDate(row.due_date)}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {modules.sales === true && <DashboardSalesCard branchId={branchId} />}

        {modules.games === true && data.leaderboard.length > 0 && (
          <Card icon={Trophy} title="המובילים החודש" href="/admin/games/points">
            <ol className="m-0 grid list-none gap-1 p-0">
              {data.leaderboard.map((row, i) => (
                <li key={row.employee_id} className="flex items-center gap-2 text-body-sm">
                  <span className="w-5 shrink-0 tabular text-muted">{i + 1}</span>
                  <span className="grow">{row.name}</span>
                  <span className="tabular">{row.points}</span>
                </li>
              ))}
            </ol>
          </Card>
        )}
      </div>

      {modules.ops_checklists === true && data.checklists.length > 0 && (
        <Panel title="צ׳קליסטים היום">
          <ul className="m-0 grid list-none gap-2 p-0">
            {data.checklists.map((row) => (
              <li
                key={`${row.branch_id}-${row.template_id}`}
                className="flex flex-wrap items-center gap-2"
              >
                <span className="grow text-body">
                  {row.branch} · {row.template}
                </span>
                <span className="text-label tabular text-muted">
                  {row.done} / {row.total}
                </span>
                {row.completed_at ? (
                  <StatusBadge tone="good">{formatDateTime(row.completed_at)}</StatusBadge>
                ) : row.run_id ? (
                  <StatusBadge tone="warn">בתהליך</StatusBadge>
                ) : (
                  <StatusBadge tone="bad">לא התחיל</StatusBadge>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </AdminPage>
  );
}

function Card({
  icon: Icon,
  title,
  count,
  tone,
  href,
  footer,
  children,
}: {
  icon: LucideIcon;
  title: string;
  count?: number;
  tone?: 'warn' | 'bad';
  href?: string;
  footer?: string;
  children: React.ReactNode;
}) {
  const body = (
    <section className="grid h-full content-start gap-2 rounded-md border border-line bg-surface p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Icon size={20} strokeWidth={1.75} aria-hidden="true" />
        <h2 className="m-0 grow text-title font-semibold">{title}</h2>
        {/* המספר תמיד עם המילה שלידו, לא לבד בצבע */}
        {count !== undefined && tone && <StatusBadge tone={tone}>{count}</StatusBadge>}
      </div>

      {children}

      {footer && <p className="m-0 text-label text-muted">{footer}</p>}
    </section>
  );

  if (!href) return body;

  return (
    <Link to={href} className="no-underline">
      {body}
    </Link>
  );
}
